import { Component, OnInit, AfterViewInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { OverlayContainer } from '@angular/cdk/overlay';
import { FormBuilder } from '@angular/forms';
import { AppNotification, NotificationType } from '@gsrs-core/main-notification';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { Title } from '@angular/platform-browser';
import jp from 'jsonpath';
import { take, map } from 'rxjs/operators';
import * as moment from 'moment';
import * as _ from 'lodash';
import { DatePipe, formatDate } from '@angular/common';

/* GSRS Imports */
import { AuthService } from '@gsrs-core/auth/auth.service';
import { ConfigService } from '@gsrs-core/config';
import { SubstanceService } from '@gsrs-core/substance/substance.service';
import { LoadingService } from '@gsrs-core/loading';
import { UtilsService } from '@gsrs-core/utils/utils.service';
import { ControlledVocabularyService } from '../../../core/controlled-vocabulary/controlled-vocabulary.service';
import { GoogleAnalyticsService } from '@gsrs-core/google-analytics';
import { MainNotificationService } from '@gsrs-core/main-notification';
import { ImpuritiesService } from '../service/impurities.service';
import { SubstanceEditImportDialogComponent } from '@gsrs-core/substance-edit-import-dialog/substance-edit-import-dialog.component';
import { JsonDialogFdaComponent } from '../../json-dialog-fda/json-dialog-fda.component';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { SubstanceFormResults } from '@gsrs-core/substance-form/substance-form.model';
import { Impurities, ImpuritiesDetails, ImpuritiesUnspecified, SubRelationship, ValidationMessage } from '../model/impurities.model';

@Component({
  selector: 'app-impurities-form',
  templateUrl: './impurities-form.component.html',
  styleUrls: ['./impurities-form.component.scss'],
  standalone: false
})
export class ImpuritiesFormComponent implements OnInit, OnDestroy {

  public ELUTION_TYPE_ISOCRATIC = 'ISOCRATIC';
  public ELUTION_TYPE_GRADIENT = 'GRADIENT';

  isLoading = true;
  showSubmissionMessages = false;
  submissionMessage: string;
  validationMessages: Array<ValidationMessage> = [];
  validationResult = false;
  private subscriptions: Array<Subscription> = [];
  copy: string;
  private overlayContainer: HTMLElement;
  serverError: boolean;
  searchValue: string;
  errorMessage: string;
  subName = 'Substance Name *';
  impurities: Impurities;
  substanceId = null;
  id?: number;
  isDisableData = false;
  username = null;
  title = null;
  submitDateMessage = '';
  statusDateMessage = '';
  canEdit: boolean = false;
  subRelationship: Array<SubRelationship> = [];
  substanceName: string;
  substanceNameHintMessage = '';
  panelExpanded = true;
  downloadJsonHref: any;
  jsonFileName: string;
  dateTypeDate: Date;

  showAdvancedSettings = false;
  showTotalImpurities = false;
  configSettingsDisplay = {};

  constructor(
    private impuritiesService: ImpuritiesService,
    private configService: ConfigService,
    private substanceService: SubstanceService,
    private authService: AuthService,
    private loadingService: LoadingService,
    private mainNotificationService: MainNotificationService,
    private gaService: GoogleAnalyticsService,
    private utilsService: UtilsService,
    private cvService: ControlledVocabularyService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private overlayContainerService: OverlayContainer,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private titleService: Title,
    private sanitizer: DomSanitizer) { }

  async ngOnInit() {
    // Get form field configuration to show on either simple or advanced form
    this.getConfigSettings();

    // Show Impurities Total Section only if at least one field is either simple or advanced
    this.showHideTotalImpuritiesSection();

    this.canEdit = await this.authService.hasSpecificPrivilege('Edit');

    this.loadingService.setLoading(true);
    this.overlayContainer = this.overlayContainerService.getContainerElement();
    this.username = this.authService.getUser();
    const routeSubscription = this.activatedRoute
      .params
      .subscribe(params => {
        if (params['id']) {
          const id = params['id'];
          this.title = 'Update Impurity';
          if (id !== this.id) {
            this.id = id;
            this.gaService.sendPageView(`Impurity Edit`);
            this.titleService.setTitle(`Update Impurities`);
            this.getImpurities();
          }
        } else { //Copy Impurities to register form
          this.title = 'Register Impurities';
          const action = this.activatedRoute.snapshot.queryParams['action'] || null;
          this.id = this.activatedRoute.snapshot.queryParams['copy'] || null;
          if (action && action === 'import' && window.history.state) {
            this.gaService.sendPageView(`Impurities Import`);
            const record = window.history.state.record;
            // this.imported = true;
            // this.getDetailsFromImport(record.record);
            // if ((record) && this.jsonValid(record)) {
            const response = JSON.parse(record);
            if (response) {
              // scrub ids and audit information before saving in import json
              this.scrub(response);

              this.impuritiesService.loadImpurities(response);
              this.impurities = this.impuritiesService.impurities;

              this.loadingService.setLoading(false);
              this.isLoading = false;
            }
            //this.substanceFormService.loadSubstance(this.substanceClass, responseImport).pipe(take(1)).subscribe(() => {
            // this.substanceSsg4mService.loadSubstance(this.subClass).pipe(take(1)).subscribe(() => {
            // this.setFormSections(formSections[this.substanceClass]);
            // });
            //  }
            //  }
          } else if (this.id) { //copy
            this.getImpurities('copy');
            this.gaService.sendPageView(`Impurities Register`);
          } else {
            setTimeout(() => {
              this.gaService.sendPageView(`Impurities Register`);
              this.titleService.setTitle(`Register Impurities`);
              this.impuritiesService.loadImpurities();
              this.impurities = this.impuritiesService.impurities;
              this.loadingService.setLoading(false);
              this.isLoading = false;
            });
          }
        }
      });
    this.subscriptions.push(routeSubscription);
  }

  ngOnDestroy(): void {
    // this.applicationService.unloadSubstance();
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }

  getConfigSettings(): void {
    // Get Impurities Config Settings from config.json file to show and hide fields in the form
    let configImpuritiesForm: any;
    configImpuritiesForm = this.configService.configData && this.configService.configData.impuritiesForm || null;

    // Get 'overview' json values from config
    const confSettings = configImpuritiesForm.settingsDisplay.overview;

    Object.keys(confSettings).forEach(key => {
      if (confSettings[key] != null) {
        if (confSettings[key] === 'simple') {
          this.configSettingsDisplay[key] = true;
        } else if (confSettings[key] === 'advanced') {
          if (this.showAdvancedSettings === true) {
            this.configSettingsDisplay[key] = true;
          } else {
            this.configSettingsDisplay[key] = false;
          }
        } else if (confSettings[key] === 'removed') {
          this.configSettingsDisplay[key] = false;
        }
      } else {
        // if either value (simple,advanced,removed) missing in config, show field on the form
        this.configSettingsDisplay[key] = true;
      }
    });
  }

  showHideTotalImpuritiesSection() {
    // Get Impurities Config Settings from config.json file to show and hide fields in the form
    let configImpuritiesForm: any;
    configImpuritiesForm = this.configService.configData && this.configService.configData.impuritiesForm || null;

    // Total Impurities Config
    // Get 'impuritiesTotal' json values from config
    const confSettingsTotal = configImpuritiesForm.settingsDisplay.impuritiesTotal || null;

    if (confSettingsTotal) {
      const keys = Object.keys(confSettingsTotal);

      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (key != null) {
          if (!this.showAdvancedSettings && confSettingsTotal[key] === 'simple') {
            this.showTotalImpurities = true;
            break;
          } else if (this.showAdvancedSettings && confSettingsTotal[key] === 'advanced') {
            this.showTotalImpurities = true;
          }
        }
      } // for loop
    } else {
      this.showTotalImpurities = true;
    }
  }

  updateAdvancedSettings(event): void {
    this.showAdvancedSettings = event.checked;

    //Get config settings
    this.getConfigSettings();

    this.showHideTotalImpuritiesSection();
  }

  getImpurities(newType?: string): void {
    if (this.id != null) {
      const id = this.id.toString();
      const getImpuritySubscribe = this.impuritiesService.getImpurities(id).subscribe(response => {
        if (response) {

          // before copying existing impurities, delete the id
          if (newType && newType === 'copy') {
            this.scrub(response);
          }
          this.impuritiesService.loadImpurities(response);
          this.impurities = this.impuritiesService.impurities;

          // Load 'Date Type Date' value to on the DatePicker Input textbox
          if (this.impurities.dateTypeDate) {
            this.dateTypeDate = new Date(this.impurities.dateTypeDate);
          }

        } else {
          this.handleProductRetrivalError();
        }
        this.loadingService.setLoading(false);
        this.isLoading = false;
      }, error => {
        this.gaService.sendException('getImpurities: error from API call');
        this.loadingService.setLoading(false);
        this.isLoading = false;
        this.handleProductRetrivalError();
      });
      this.subscriptions.push(getImpuritySubscribe);
    }
  }

  validate(): void {
    this.isLoading = true;
    this.serverError = false;
    this.loadingService.setLoading(true);

    // Check validation on Client side.
    this.validateClient();

    // If there is no error on client side, check validation on server side
    if (this.validationMessages.length === 0) {
      // this.impuritiesService.validateImpurities().pipe(take(1)).subscribe(results => {
      this.submissionMessage = null;
      //   this.validationMessages = results.validationMessages.filter(
      //      message => message.messageType.toUpperCase() === 'ERROR' || message.messageType.toUpperCase() === 'WARNING');
      //   this.validationResult = results.valid;

      this.validationResult = true;
      this.showSubmissionMessages = true;
      this.loadingService.setLoading(false);
      this.isLoading = false;


      if (this.validationMessages.length === 0 && this.validationResult === true) {
        this.submissionMessage = 'Impurities is Valid. Would you like to submit?';
      }
      /* }, error => {
         this.addServerError(error);
         this.loadingService.setLoading(false);
         this.isLoading = false;
       });
       */
    }
    //  }
  }

  setValidationMessage(message: string) {
    const validate: ValidationMessage = {};
    validate.message = message;
    validate.messageType = 'ERROR';
    this.validationMessages.push(validate);
    this.validationResult = false;
  }

  // Validate data in client side first
  validateClient(): void {
    this.validationMessages = [];
    this.validationResult = true;

    // Date Type Date field should be valid date
    if (this.impurities.dateTypeDate) {
      const isValidDateTypeDate = this.validateDate(this.impurities.dateTypeDate);
      if (isValidDateTypeDate === false) {
        this.setValidationMessage('Date Type Date is invalid');
      }
    }

    // Validate Subsance
    if (this.impurities.impuritiesSubstanceList.length === 0) {
      this.setValidationMessage('Substance Name is required');
    }

    // Validate Substance Name
    if (this.impurities != null) {
      this.impurities.impuritiesSubstanceList.forEach((elementSub, index) => {
        if (elementSub != null) {
          if (elementSub.substanceUuid == null) {
            this.setValidationMessage('Substance Name (' + (index + 1) + ') is required');
          }
        }

        elementSub.impuritiesTestList.forEach(elementTest => {

          // if the elutionType value is not 'Isocratic' or 'Gradient' and if there is data in Mobile Phase table,
          // empty the list
          if (elementTest.elutionType) {
            if (elementTest.elutionType.toUpperCase() !== this.ELUTION_TYPE_ISOCRATIC &&
              elementTest.elutionType.toUpperCase() !== this.ELUTION_TYPE_GRADIENT) {
              if (elementTest.impuritiesSolutionTableList) {
                if (elementTest.impuritiesSolutionTableList.length > 0) {
                  elementTest.impuritiesSolutionTableList = [];
                }
              }
            }
          }

          elementTest.impuritiesSolutionTableList.forEach((solTable, indexSolTable) => {
            if (solTable) {

              // Each row should total to 100 %
              let totalCount100percent: number = 0;

              if (solTable.solutionTime) {
                if (this.isNumber(solTable.solutionTime) === false) {
                  this.setValidationMessage('Time (min) should be number in Mobile Phase for row ' + (indexSolTable + 1));
                }
              }
              if (solTable.solutionAPercent) {
                if (this.isNumber(solTable.solutionAPercent) === false) {
                  this.setValidationMessage('Solution A (%) should be number in Mobile Phase for row ' + (indexSolTable + 1));
                } else {
                  totalCount100percent = totalCount100percent + Number(solTable.solutionAPercent);
                }
              }
              if (solTable.solutionBPercent) {
                if (this.isNumber(solTable.solutionBPercent) === false) {
                  this.setValidationMessage('Solution B (%) should be number in Mobile Phase for row ' + (indexSolTable + 1));
                } else {
                  totalCount100percent = totalCount100percent + Number(solTable.solutionBPercent);
                }
              }
              if (solTable.solutionCPercent) {
                if (this.isNumber(solTable.solutionCPercent) === false) {
                  this.setValidationMessage('Solution C (%) should be number in Mobile Phase for row ' + (indexSolTable + 1));
                } else {
                  totalCount100percent = totalCount100percent + Number(solTable.solutionCPercent);
                }
              }
              if (solTable.solutionDPercent) {
                if (this.isNumber(solTable.solutionDPercent) === false) {
                  this.setValidationMessage('Solution D (%) should be number in Mobile Phase for row ' + (indexSolTable + 1));
                } else {
                  totalCount100percent = totalCount100percent + Number(solTable.solutionDPercent);
                }
              }
              if (solTable.solutionEPercent) {
                if (this.isNumber(solTable.solutionEPercent) === false) {
                  this.setValidationMessage('Solution E (%) should be number in Mobile Phase for row ' + (indexSolTable + 1));
                } else {
                  totalCount100percent = totalCount100percent + Number(solTable.solutionEPercent);
                }
              }
              if (solTable.solutionFPercent) {
                if (this.isNumber(solTable.solutionFPercent) === false) {
                  this.setValidationMessage('Solution F (%) should be number in Mobile Phase for row ' + (indexSolTable + 1));
                }
                else {
                  totalCount100percent = totalCount100percent + Number(solTable.solutionFPercent);
                }
              }
              if (solTable.solutionGPercent) {
                if (this.isNumber(solTable.solutionGPercent) === false) {
                  this.setValidationMessage('Solution G (%) should be number in Mobile Phase for row ' + (indexSolTable + 1));
                } else {
                  totalCount100percent = totalCount100percent + Number(solTable.solutionGPercent);
                }
              }
              if (solTable.solutionHPercent) {
                if (this.isNumber(solTable.solutionHPercent) === false) {
                  this.setValidationMessage('Solution H (%) should be number in Mobile Phase for row ' + (indexSolTable + 1));
                } else {
                  totalCount100percent = totalCount100percent + Number(solTable.solutionHPercent);
                }
              }
              if (solTable.solutionIPercent) {
                if (this.isNumber(solTable.solutionIPercent) === false) {
                  this.setValidationMessage('Solution I (%) should be number in Mobile Phase for row ' + (indexSolTable + 1));
                } else {
                  totalCount100percent = totalCount100percent + Number(solTable.solutionIPercent);
                }
              }
              if (solTable.solutionJPercent) {
                if (this.isNumber(solTable.solutionJPercent) === false) {
                  this.setValidationMessage('Solution J (%) should be number in Mobile Phase for row ' + (indexSolTable + 1));
                } else {
                  totalCount100percent = totalCount100percent + Number(solTable.solutionJPercent);
                }
              }

              /*
              if (totalCount100percent != 100) {
                this.setValidationMessage('The sum of the % columns should be 100% in Mobile Phase for row ' + (indexSolTable + 1));
              } */
            }
          }); // For Loop impuritiesSolutionTableList

        }); // For Loop impuritiesTestList

      }); // For Loop impuritiesSubstanceList
    } // if impurities exists


    if (this.validationMessages.length > 0) {
      this.showSubmissionMessages = true;
      this.loadingService.setLoading(false);
      this.isLoading = false;
    }

  }

  toggleValidation(): void {
    this.showSubmissionMessages = !this.showSubmissionMessages;
  }

  addServerError(error: any): void {
    this.serverError = true;
    this.validationResult = false;
    this.validationMessages = null;

    const message: ValidationMessage = {
      actionType: 'server failure',
      links: [],
      appliedChange: false,
      suggestedChange: false,
      messageType: 'ERROR',
      message: 'Unknown Server Error'
    };
    if (error && error.error && error.error.message) {
      message.message = 'Server Error ' + (error.status + ': ' || ': ') + error.error.message;
    } else if (error && error.error && (typeof error.error) === 'string') {
      message.message = 'Server Error ' + (error.status + ': ' || '') + error.error;
    } else if (error && error.message) {
      message.message = 'Server Error ' + (error.status + ': ' || '') + error.message;
    }
    this.validationMessages = [message];
    this.showSubmissionMessages = true;
  }

  private handleProductRetrivalError() {
    const notification: AppNotification = {
      message: 'The impurities you\'re trying to edit doesn\'t exist.',
      type: NotificationType.error,
      milisecondsToShow: 4000
    };
    this.mainNotificationService.setNotification(notification);
    setTimeout(() => {
      this.router.navigate(['/impurities/register']);
      this.impuritiesService.loadImpurities();
    }, 5000);
  }

  submit(): void {
    this.isLoading = true;
    this.loadingService.setLoading(true);

    this.impuritiesService.saveImpurities().subscribe(response => {
      this.loadingService.setLoading(false);
      this.isLoading = false;
      this.validationMessages = null;
      this.submissionMessage = 'Impurities was saved successfully!';
      this.showSubmissionMessages = true;
      this.validationResult = false;
      setTimeout(() => {
        this.showSubmissionMessages = false;
        this.submissionMessage = '';
        if (response.id) {
          this.impuritiesService.bypassUpdateCheck();
          const id = response.id;
          this.router.routeReuseStrategy.shouldReuseRoute = () => false;
          this.router.onSameUrlNavigation = 'reload';
          //  this.router.navigate(['/impuritiesDetails', id, 'edit']);
          this.router.navigate(['/impurities', id]);
        }
      }, 4000);
    }, (error: SubstanceFormResults) => {
      this.showSubmissionMessages = true;
      this.loadingService.setLoading(false);
      this.isLoading = false;
      this.submissionMessage = null;
      if (error.validationMessages && error.validationMessages.length) {
        this.validationResult = error.isSuccessfull;
        this.validationMessages = error.validationMessages
          .filter(message => message.messageType.toUpperCase() === 'ERROR' || message.messageType.toUpperCase() === 'WARNING');
        this.showSubmissionMessages = true;
      } else {
        this.submissionMessage = 'There was a problem with your submission';
        this.addServerError(error.serverError);
        setTimeout(() => {
          this.showSubmissionMessages = false;
          this.submissionMessage = null;
        }, 8000);
      }
    }
    );
  }

  addNewImpuritiesSubstance(event: Event) {
    event.stopPropagation();

    this.impuritiesService.addNewImpuritiesSubstance();
  }

  addNewImpuritiesTotal() {
    this.impuritiesService.addNewImpuritiesTotal();
  }

  confirmDeleteImpurities() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: { message: 'Are you sure you want to delete this Impurities?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result === true) {
        this.deleteImpurities();
      }
    });
  }

  deleteImpurities(): void {
    this.impuritiesService.deleteImpurities().subscribe(response => {
      this.impuritiesService.bypassUpdateCheck();
      this.displayMessageAfterDeleteImpurities();
    }, (err) => {
      console.log(err);
    }
    );
  }

  displayMessageAfterDeleteImpurities() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: 'This impurities record was deleted successfully',
        type: 'home'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      this.router.navigate(['/home']);
    });
  }

  showJSON(): void {
    const date = new Date();
    let jsonFilename = 'impurities_' + moment(date).format('MMM-DD-YYYY_H-mm-ss');

    let cleanImpurities = this.cleanImpurities();

    let data = { jsonData: cleanImpurities, jsonFilename: jsonFilename };

    const dialogRef = this.dialog.open(JsonDialogFdaComponent, {
      width: '90%',
      height: '90%',
      data: data
    });

    //   this.overlayContainer.style.zIndex = '1002';
    const dialogSubscription = dialogRef.afterClosed().subscribe(response => {
    });
    this.subscriptions.push(dialogSubscription);
  }

  saveJSON(): void {
    // apply the same cleaning to remove deleted objects and return what will be sent to the server on validation / submission
    let json = this.cleanImpurities();

    const uri = this.sanitizer.bypassSecurityTrustUrl('data:text/json;charset=UTF-8,' + encodeURIComponent(JSON.stringify(json)));
    this.downloadJsonHref = uri;

    const date = new Date();
    this.jsonFileName = 'impurities_' + moment(date).format('MMM-DD-YYYY_H-mm-ss');
  }

  importJSON(): void {
    let data: any;
    data = {
      title: 'Impurities Record Import',
      entity: 'Impurities',
    };
    const dialogRef = this.dialog.open(SubstanceEditImportDialogComponent, {
      width: '650px',
      autoFocus: false,
      data: data
    });
    this.overlayContainer.style.zIndex = '1002';

    const dialogSubscription = dialogRef.afterClosed().pipe(take(1)).subscribe(response => {
      if (response) {
        this.loadingService.setLoading(true);
        this.overlayContainer.style.zIndex = null;

        // attempting to reload a substance without a router refresh has proven to cause issues with the relationship dropdowns
        // There are probably other components affected. There is an issue with subscriptions likely due to some OnInit not firing

        /* const read = JSON.parse(response);
         if (this.id && read.uuid && this.id === read.uuid) {
           this.substanceFormService.importSubstance(read, 'update');
           this.submissionMessage = null;
           this.validationMessages = [];
           this.showSubmissionMessages = false;
           this.loadingService.setLoading(false);
           this.isLoading = false;
         } else {
         if ( read.substanceClass === this.substanceClass) {
           this.imported = true;
           this.substanceFormService.importSubstance(read);
           this.submissionMessage = null;
           this.validationMessages = [];
           this.showSubmissionMessages = false;
           this.loadingService.setLoading(false);
           this.isLoading = false;
         } else {*/
        setTimeout(() => {
          this.router.onSameUrlNavigation = 'reload';
          this.loadingService.setLoading(false);
          if (!this.id) {
            // new record
            this.router.navigateByUrl('/impurities/register?action=import', { state: { record: response } });
          }
        }, 1000);
      }
      // }
      // }
    });
  }

  changeDateTypeDate(event: MatDatepickerInputEvent<Date>): void {
    const inputElement: HTMLElement = event.targetElement;
    const inputValue: any = (inputElement as HTMLInputElement).value;

    this.impurities.dateTypeDate = inputValue;

  }

  increaseOverlayZindex(): void {
    this.overlayContainer.style.zIndex = '1002';
  }

  decreaseOverlayZindex(): void {
    this.overlayContainer.style.zIndex = null;
  }

  validateDate(dateinput: any): boolean {
    let isValid = true;
    if ((dateinput !== null) && (dateinput.length > 0)) {
      if ((dateinput.length < 8) || (dateinput.length > 10)) {
        return false;
      }
      const split = dateinput.split('/');
      if (split.length !== 3 || (split[0].length < 1 || split[0].length > 2) ||
        (split[1].length < 1 || split[1].length > 2) || split[2].length !== 4) {
        return false;
      }
      if (split.length === 3) {
        const comstring = split[0] + split[1] + split[2];
        for (let i = 0; i < split.length; i++) {
          const valid = this.isNumber(split[i]);
          if (valid === false) {
            isValid = false;
            break;
          }
        }
      }
    }
    return isValid;
  }

  isNumber(str: any): boolean {
    if (str) {
      const num = Number(str);
      const nan = isNaN(num);
      return !nan;
    }
    return false;
  }

  cleanImpurities(): any {
    const copiedImpurities = _.cloneDeep(this.impurities);

    delete copiedImpurities._dateTypeDate;

    return copiedImpurities;
  }

  scrub(oldraw: any): any {
    const old = oldraw;

    const idHolders = jp.query(old, '$..[?(@.id)]');
    for (let i = 0; i < idHolders.length; i++) {
      if (idHolders[i].id) {
        delete idHolders[i].id;
      }
    }

    const createHolders = jp.query(old, '$..[?(@.creationDate)]');
    for (let i = 0; i < createHolders.length; i++) {
      delete createHolders[i].creationDate;
    }

    const createdByHolders = jp.query(old, '$..[?(@.createdBy)]');
    for (let i = 0; i < createdByHolders.length; i++) {
      delete createdByHolders[i].createdBy;
    }

    const modifyHolders = jp.query(old, '$..[?(@.lastModifiedDate)]');
    for (let i = 0; i < modifyHolders.length; i++) {
      delete modifyHolders[i].lastModifiedDate;
    }

    const modifiedByHolders = jp.query(old, '$..[?(@.modifiedBy)]');
    for (let i = 0; i < modifiedByHolders.length; i++) {
      delete modifiedByHolders[i].modifiedBy;
    }

    const intVersionHolders = jp.query(old, '$..[?(@.internalVersion)]');
    for (let i = 0; i < intVersionHolders.length; i++) {
      delete intVersionHolders[i].internalVersion;
    }

    delete old['id'];
    delete old['creationDate'];
    delete old['createdBy'];
    delete old['modifiedBy'];
    delete old['lastModifiedDate'];
    delete old['internalVersion'];
    delete old['$$update'];
    delete old['_self'];

    return old;
  }

}